import React, { createContext, FC, useContext, useState } from 'react';
import useTypedPage from '@/Hooks/useTypedPage';
import { router } from '@inertiajs/core';
import useRoute from '@/Hooks/useRoute';
import { DarkTheme, LightTheme } from '@/Editor/CodeThemes';

interface ThemeContextProps {
  theme: App.Enums.Theme;
  otherTheme: App.Enums.Theme;
  editorTheme: {};
  toggleTheme: (user?: App.Data.UserData) => void;
  defineThemes: (editor: any) => void;
  codeHighlight: any;
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeContext = createContext<ThemeContextProps>({
  theme: 'dark',
  otherTheme: 'light',
  editorTheme: 'codewp-dark',
  toggleTheme: (user?: App.Data.UserData) => {},
  defineThemes: () => {},
  codeHighlight: DarkTheme,
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error(`useTheme must be used within an ThemeProvider`);
  }
  return context;
};

export const ThemeProvider: FC<ThemeProviderProps> = ({ children }) => {
  const page = useTypedPage();
  const route = useRoute();
  const defaultTheme =
    page.props.auth?.user?.theme || ('dark' as App.Enums.Theme);
  const [theme, setTheme] = useState<App.Enums.Theme>(defaultTheme);
  const otherTheme = theme === 'dark' ? 'light' : ('dark' as App.Enums.Theme);
  const editorTheme = theme === 'dark' ? 'codewp-dark' : 'codewp-light';
  const codeHighlight = theme === 'dark' ? DarkTheme : LightTheme;

  const toggleTheme = (user?: App.Data.UserData) => {
    if (user) {
      router.put(
        route('user.update', user.id),
        { theme: otherTheme },
        {
          preserveState: false,
          onSuccess: () => setTheme(otherTheme),
        }
      );
    } else {
      setTheme(otherTheme);
    }
  };

  const defineThemes = (editor: any) => {
    editor.defineTheme('codewp-dark', editorThemes['codewp-dark']);
    editor.defineTheme('codewp-light', editorThemes['codewp-light']);
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        otherTheme,
        toggleTheme,
        defineThemes,
        editorTheme,
        codeHighlight,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

const editorThemes = {
  'codewp-dark': {
    base: 'vs-dark',
    inherit: true,
    rules: [
      { background: '#0A0A0A' },
      { token: 'metatag.php', foreground: '#FB5D4B' }, // PHP Opening and Closing Tag.
      { token: 'keyword.php', foreground: '#81e885' }, // PHP General Keywords, if, return, function, etc.
      { token: 'string', foreground: '#8787FD' }, // PHP String.
      { token: 'variable.php', foreground: '#81E885' }, // PHP Variable.
      { token: 'identifier.php', foreground: '#818181' }, // PHP Function Identifier.
      { token: 'tag', foreground: '#81E885' }, // HTML Element Name.
      { token: 'attribute.name', foreground: '#81E885' }, // HTML Attribute Name.
      { token: 'attribute.value', foreground: '#8787FD' }, // HTML Attribute Value.
    ],
    colors: {
      'editor.background': '#0A0A0A',
    },
  },
  'codewp-light': {
    base: 'vs',
    inherit: true,
    rules: [
      { background: '#FFFFFF' },
      { token: 'metatag.php', foreground: '#FF0000', fontStyle: 'bold' }, // PHP Opening and Closing Tag.
      { token: 'keyword.php', foreground: '#0100B6', fontStyle: 'bold' }, // PHP General Keywords, if, return, function, etc.
      { token: 'string', foreground: '#D80800' }, // PHP String.
      { token: 'variable.php', foreground: '#0206FF', fontStyle: 'italic' }, // PHP Variable.
      { token: 'identifier.php', foreground: '#0A0A0A' }, // PHP Function Identifier.
      { token: 'tag', foreground: '#800000' }, // HTML Element Name.
      { token: 'attribute.name', foreground: '#FF0000' }, // HTML Attribute Name.
      { token: 'attribute.value', foreground: '#0451A5' }, // HTML Attribute Value.
      { token: 'keyword', foreground: '#0100B6', fontStyle: 'bold' },
      { token: 'comment', foreground: '#00B418' },
    ],
    colors: {
      'editor.background': '#FFFFFF',
    },
  },
};
